import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/styles';

import { GENERIC_REQUEST_ERROR } from 'constants/errors';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  error: PropTypes.shape({
    error: PropTypes.string.isRequired,
    errorDescription: PropTypes.string.isRequired,
  }).isRequired,
  errorStatusCode: PropTypes.string,
};

const defaultProps = {
  errorStatusCode: '',
};

export function UnstyledErrorPage({ classes, error, errorStatusCode }) {
  const err = error || {};
  const customBadRequestError =
    err.errorDescription && err.error === 'access_denied' ? err.errorDescription : 'Bad Request';

  // Specific to user invite token
  let customError401 = "You don't have permissions to view this page."
  if (err.error === 'Expired token')
    customError401 = "This invite has expired. Please request a new invite."
  else if (err.error === 'Invalid token' || err.error === 'Invalid key')
    customError401 = "Invalid link. Please request a new invite."

  const errorStatusList = {
    400: {
      image: '/images/errors/400Error.svg',
      text: customBadRequestError,
    },
    401: {
      image: '/images/errors/401Error.svg',
      text: customError401,
    },
    403: {
      image: '/images/errors/403Error.svg',
      text: 'Forbidden',
    },
    404: {
      image: '/images/errors/404Error.svg',
      text: "We can't find the page you were looking for.",
    },
    requestError: {
      image: '/images/logos/robot-dropped-icecream.png',
      text: GENERIC_REQUEST_ERROR,
    },
    default: {
      image: '/images/logos/robot-dropped-icecream.png',
      text: 'Something went wrong. Sorry about that.',
    },
  };

  const errorStatus = errorStatusList[errorStatusCode] || errorStatusList.default;

  return (
    <div className={classes.container}>
      <div className={classes.background} />
      <div className={classes.contentContainer}>
        <div className={classes.logoContainer}>
          <img src="/images/logos/or-ascii.svg" className={classes.logo} alt="Object Rocket" />
        </div>
        <div className={classes.imageContainer}>
          <div className={classes.rocketContainer}>
            <img
              src="/images/rockets/rocket-step3.svg"
              className={classes.rocket}
              alt="rocket logo"
            />
          </div>
          <div className={classes.errorStatusContainer}>
            {errorStatus !== errorStatusList.default && errorStatusCode !== 'requestError' ? (
              <img
                src={errorStatus.image}
                className={classes.errorStatus}
                alt={`${errorStatusCode} status code`}
              />
            ) : (
              <img src={errorStatus.image} className={classes.robot} alt="sad robot" />
            )}
          </div>
        </div>
        <div className={classes.textContainer}>
          <Typography variant="h2" className={classes.oops}>
            Oops!
          </Typography>
          <Typography variant="h4" className={classes.errorMessage}>
            {errorStatus.text}
          </Typography>
        </div>
      </div>
    </div>
  );
}

UnstyledErrorPage.propTypes = propTypes;
UnstyledErrorPage.defaultProps = defaultProps;

const styles = (theme) => ({
  container: {
    // cover the MissionControl content class padding
    margin: theme.spacing(-1),
    overflow: 'hidden',
    backgroundColor: '#1d191a',
    backgroundSize: 'cover',
    height: '100%',
  },
  background: {
    background: 'url("/images/errors/long-sliding-stars.png")',
    backgroundSize: 'contain',
    height: '100vh',
    width: '10000px',
    animation: 'slide 60s linear infinite',
  },
  '@keyframes slide': {
    '0%': {
      transform: 'translate3d(0, 0, 0)',
    },
    '100%': {
      transform: 'translate3d(-7500px, 0, 0)',
    },
  },
  contentContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    padding: `${theme.spacing(3)}px ${theme.spacing(3)}px`,
    display: 'flex',
    flexFlow: 'column nowrap',
    width: '100%',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  logo: {
    width: '100%',
    height: 'auto',
    maxHeight: '100px',
  },
  imageContainer: {
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'space-between',
    flex: '1 1 50%',
    width: '100%',
    height: 'auto',
  },
  rocketContainer: {
    display: 'flex',
    alignItems: 'space-around',
    justifyContent: 'center',
    flex: '1 1 25%',
    paddingTop: theme.spacing(3),
    paddingRight: theme.spacing(1),
  },
  rocket: {
    width: '100%',
    height: 'auto',
    maxHeight: theme.spacing(5),
  },
  errorStatusContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: '1 1 75%',
  },
  errorStatus: {
    width: '100%',
    height: 'auto',
    maxHeight: '400px',
  },
  robot: {
    maxWidth: '400px',
    maxHeight: '400px',
    height: 'auto',
    width: '100%',
  },
  textContainer: {
    display: 'flex',
    width: '100%',
    flexFlow: 'column nowrap',
    flex: '1 1 auto',
  },
  oops: {
    display: 'flex',
    width: '100%',
    color: '#f7a80a',
    fontFamily: 'Courier',
    justifyContent: 'center',
  },
  errorMessage: {
    display: 'flex',
    width: '100%',
    color: '#f7a80a',
    fontFamily: 'Courier',
    paddingTop: theme.spacing(2),
    justifyContent: 'center',
  },
});

export default withStyles(styles)(UnstyledErrorPage);
